import React from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Chip,
    Button, Stack, Typography, Card, CardContent,Box
} from "@mui/material";
import type { SavedArtworkViewListItemDto } from "../../models/SaveArtworkViewRequests";

type SavedArtworkViewsDialogProps = {
    open: boolean;
    onClose: () => void;
    views: SavedArtworkViewListItemDto[];
    loading?: boolean;
    activeSavedViewId?: number | null;
    onLoad: (id: number) => Promise<void>;
    onDelete: (id: number) => Promise<void>;
    onUpdate?: (id: number) => Promise<void>;
};

const SavedArtworkViewsDialog: React.FC<SavedArtworkViewsDialogProps> = ({
    open,
    onClose,
    views,
    loading = false,
    activeSavedViewId,
    onLoad,
    onDelete,
    onUpdate
}) => {
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Saved Views</DialogTitle>

            <DialogContent dividers>
                {loading ? (
                    <Typography color="text.secondary">Loading…</Typography>
                ) : views.length === 0 ? (
                    <Typography color="text.secondary">No saved views yet.</Typography>
                ) : (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                            gap: 1.5
                        }}
                    >
                        {views.map((view) => {
                            const isActive = activeSavedViewId === view.id;

                            return (
                                <Card
                                    key={view.id}
                                    variant="outlined"
                                    sx={{
                                        borderRadius: 2,
                                        borderColor: isActive ? "primary.main" : "#e8e3db",
                                        boxShadow: isActive ? "0 4px 14px rgba(0,0,0,0.08)" : "0 1px 2px rgba(0,0,0,0.04)"
                                    }}
                                >
                                    <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                                        <Box>
                                            <Stack
                                                direction="row"
                                                justifyContent="space-between"
                                                alignItems="flex-start"
                                                spacing={1}
                                            >
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                    {view.name}
                                                </Typography>

                                                {isActive && (
                                                    <Chip
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                        label="Active"
                                                    />
                                                )}
                                            </Stack>

                                            <Typography variant="body2" color="text.secondary">
                                                {view.clientName}
                                                {` • ${view.portfolioName ?? "Full Collection"}`}
                                            </Typography>

                                            <Typography variant="caption" color="text.secondary">
                                                Updated {new Date(view.updatedAtUtc).toLocaleString()}
                                            </Typography>
                                        </Box>

                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={() => onLoad(view.id)}
                                            >
                                                Load
                                            </Button>

                                            {onUpdate && (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => onUpdate(view.id)}
                                                >
                                                    Update
                                                </Button>
                                            )}

                                            <Button
                                                color="error"
                                                size="small"
                                                onClick={() => onDelete(view.id)}
                                            >
                                                Delete
                                            </Button>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default SavedArtworkViewsDialog; 